import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getClients, getProjects, createInvoice, sendInvoice, getUninvoicedTimeEntries } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/ui/page-header';
import { SectionRule } from '@/components/ui/section-rule';
import { Field } from '@/components/ui/field';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const formatCurrency = (a) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(a || 0);

export default function NewInvoice() {
  const [searchParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedClient, setSelectedClient] = useState(searchParams.get('client') || '');
  const [selectedProject, setSelectedProject] = useState(searchParams.get('project') || '');
  const [lineItems, setLineItems] = useState([{ description: '', qty: 1, rate: 0, amount: 0 }]);
  const [taxPercent, setTaxPercent] = useState(0);
  const [dueDate, setDueDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedInvoice, setSavedInvoice] = useState(null);
  const [timeEntryIds, setTimeEntryIds] = useState([]);
  const [loadingTimeEntries, setLoadingTimeEntries] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    getClients().then(r => setClients(r.data)).catch(() => {});
    getProjects().then(r => setProjects(r.data)).catch(() => {});
  }, []);

  // Auto-populate from time entries when project changes
  const handleProjectChange = async (projectId) => {
    setSelectedProject(projectId);
    if (projectId && projectId !== 'none') {
      setLoadingTimeEntries(true);
      try {
        const res = await getUninvoicedTimeEntries(projectId);
        const entries = res.data;
        if (entries.length > 0) {
          const items = entries.map(e => ({
            description: e.description,
            qty: e.hours,
            rate: e.rate || 0,
            amount: e.amount || (e.hours * (e.rate || 0))
          }));
          setLineItems(items);
          setTimeEntryIds(entries.map(e => e.id));
          toast.success(`Populated ${entries.length} time entries as line items`);
        }
      } catch { /* ignore */ }
      setLoadingTimeEntries(false);
    }
  };

  // Keeps whatever the user typed (including '') so backspacing a 0 or 1
  // actually clears the box instead of snapping straight back — only the
  // derived `amount` needs a real number, not the qty/rate fields themselves.
  const updateLineItem = (idx, field, value) => {
    setLineItems(prev => {
      const items = [...prev];
      items[idx] = { ...items[idx], [field]: value };
      if (field === 'qty' || field === 'rate') {
        items[idx].amount = (parseFloat(items[idx].qty) || 0) * (parseFloat(items[idx].rate) || 0);
      }
      return items;
    });
  };

  // Normalizes a blank qty/rate back to 0 once the user leaves the field,
  // so a saved invoice never carries an empty-string quantity or rate.
  const blurLineItem = (idx, field) => {
    setLineItems(prev => {
      const items = [...prev];
      if (items[idx][field] === '') {
        items[idx] = { ...items[idx], [field]: 0 };
      }
      return items;
    });
  };

  const addLineItem = () => setLineItems(prev => [...prev, { description: '', qty: 1, rate: 0, amount: 0 }]);
  const removeLineItem = (idx) => setLineItems(prev => prev.filter((_, i) => i !== idx));

  const subtotal = lineItems.reduce((s, item) => s + (item.amount || 0), 0);
  const taxAmount = subtotal * ((parseFloat(taxPercent) || 0) / 100);
  const total = subtotal + taxAmount;

  const handleSave = async () => {
    if (!selectedClient) { toast.error('Select a client'); return; }
    if (lineItems.some(i => !i.description.trim())) { toast.error('All line items need a description'); return; }
    setSaving(true);
    try {
      const res = await createInvoice({
        client_id: selectedClient,
        project_id: selectedProject && selectedProject !== 'none' ? selectedProject : undefined,
        line_items: lineItems.map(item => ({
          ...item,
          qty: parseFloat(item.qty) || 0,
          rate: parseFloat(item.rate) || 0,
        })),
        tax_percent: parseFloat(taxPercent) || 0,
        due_date: dueDate || undefined,
        currency: 'USD',
        time_entry_ids: timeEntryIds.length > 0 ? timeEntryIds : undefined
      });
      setSavedInvoice(res.data);
      toast.success('Invoice created');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to create invoice';
      toast.error(msg);
    }
    setSaving(false);
  };

  const handleSend = async () => {
    if (!savedInvoice) return;
    try {
      const res = await sendInvoice(savedInvoice.id);
      setSavedInvoice(res.data);
      toast.success('Invoice sent!');
    } catch { toast.error('Failed to send invoice'); }
  };

  const copyPayLink = () => {
    if (!savedInvoice) return;
    navigator.clipboard.writeText(`${window.location.origin}/pay/${savedInvoice.token}`);
    toast.success('Payment link copied');
  };

  return (
    <div className="mx-auto max-w-[760px]" data-testid="new-invoice-page">
      <button
        onClick={() => navigate('/invoices')}
        className="mb-4 inline-flex items-center gap-1.5 text-small text-ink-muted transition-colors hover:text-forest"
      >
        <ArrowLeft strokeWidth={1.5} className="h-3.5 w-3.5" /> Invoices
      </button>

      <PageHeader title={savedInvoice ? `Invoice #${savedInvoice.invoice_number}` : 'New invoice'} />

      {savedInvoice ? (
        <section data-testid="saved-invoice">
          <SectionRule label="Created" />
          <p className="font-display text-page-title text-ink">Invoice #{savedInvoice.invoice_number}</p>
          <dl className="mt-6 grid grid-cols-2 gap-6">
            <div>
              <dt className="text-micro uppercase text-ink-subtle">Total</dt>
              <dd className="mt-1 font-sans text-metric tabular-nums text-ink">{formatCurrency(savedInvoice.total)}</dd>
            </div>
            <div>
              <dt className="text-micro uppercase text-ink-subtle">Status</dt>
              <dd className="mt-1 text-body capitalize text-ink">{savedInvoice.status}</dd>
            </div>
          </dl>
          <div className="mt-8 flex flex-col gap-3 border-t border-line pt-6 sm:flex-row">
            {savedInvoice.status === 'draft' && (
              <Button onClick={handleSend} data-testid="send-invoice-btn">Send invoice</Button>
            )}
            {savedInvoice.status === 'sent' && (
              <Button onClick={copyPayLink} variant="outline" data-testid="copy-pay-link">Copy payment link</Button>
            )}
            <Button variant="ghost" onClick={() => navigate('/invoices')}>Back to invoices</Button>
          </div>
        </section>
      ) : (
        <>
          <section>
            <SectionRule label="Client" />
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field label="Client" htmlFor="invoice-client" hint="Required">
                <Select value={selectedClient} onValueChange={setSelectedClient}>
                  <SelectTrigger id="invoice-client" data-testid="invoice-client-select">
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field
                label="Project"
                htmlFor="invoice-project"
                hint={
                  loadingTimeEntries ? 'Loading time entries…'
                    : timeEntryIds.length > 0 ? `${timeEntryIds.length} time entries auto-populated as line items`
                      : 'Optional'
                }
              >
                <Select value={selectedProject} onValueChange={handleProjectChange}>
                  <SelectTrigger id="invoice-project" data-testid="invoice-project-select">
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No project</SelectItem>
                    {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>
          </section>

          <section className="mt-10">
            <SectionRule
              label="Line items"
              action={
                <Button variant="outline" size="sm" onClick={addLineItem} data-testid="add-line-item">Add row</Button>
              }
            />
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Description</TableHead>
                  <TableHead className="w-[90px] text-right">Qty</TableHead>
                  <TableHead className="w-[110px] text-right">Rate</TableHead>
                  <TableHead className="w-[110px] text-right">Amount</TableHead>
                  <TableHead className="w-10"><span className="sr-only">Remove</span></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lineItems.map((item, i) => (
                  <TableRow key={i} className="hover:bg-transparent" data-testid={`line-item-${i}`}>
                    <TableCell className="px-1">
                      <Input
                        value={item.description}
                        onChange={e => updateLineItem(i, 'description', e.target.value)}
                        placeholder="Description"
                        aria-label={`Line item ${i + 1} description`}
                      />
                    </TableCell>
                    <TableCell className="px-1">
                      <Input
                        type="number"
                        value={item.qty}
                        onChange={e => updateLineItem(i, 'qty', e.target.value)}
                        onBlur={() => blurLineItem(i, 'qty')}
                        className="text-right tabular-nums"
                        aria-label={`Line item ${i + 1} quantity`}
                      />
                    </TableCell>
                    <TableCell className="px-1">
                      <Input
                        type="number"
                        value={item.rate}
                        onChange={e => updateLineItem(i, 'rate', e.target.value)}
                        onBlur={() => blurLineItem(i, 'rate')}
                        className="text-right tabular-nums"
                        aria-label={`Line item ${i + 1} rate`}
                      />
                    </TableCell>
                    <TableCell className="text-right text-ink">{formatCurrency(item.amount)}</TableCell>
                    <TableCell className="px-1 text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-ink-subtle hover:text-destructive"
                        onClick={() => removeLineItem(i)}
                        disabled={lineItems.length === 1}
                        aria-label={`Remove line item ${i + 1}`}
                      >
                        <Trash2 strokeWidth={1.5} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </section>

          <section className="mt-10">
            <SectionRule label="Totals" />
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field label="Tax %" htmlFor="invoice-tax">
                <Input
                  id="invoice-tax"
                  type="number"
                  value={taxPercent}
                  onChange={e => setTaxPercent(e.target.value)}
                  onBlur={() => setTaxPercent(prev => prev === '' ? 0 : prev)}
                  className="tabular-nums"
                  data-testid="tax-input"
                />
              </Field>
              <Field label="Due date" htmlFor="invoice-due-date">
                <Input
                  id="invoice-due-date"
                  type="date"
                  value={dueDate}
                  onChange={e => setDueDate(e.target.value)}
                  className="tabular-nums"
                  data-testid="due-date-input"
                />
              </Field>
            </div>

            <dl className="ml-auto mt-8 max-w-[280px] space-y-2">
              <div className="flex justify-between">
                <dt className="text-small text-ink-muted">Subtotal</dt>
                <dd className="text-small tabular-nums text-ink">{formatCurrency(subtotal)}</dd>
              </div>
              {taxPercent > 0 && (
                <div className="flex justify-between">
                  <dt className="text-small text-ink-muted">Tax ({taxPercent}%)</dt>
                  <dd className="text-small tabular-nums text-ink">{formatCurrency(taxAmount)}</dd>
                </div>
              )}
              <div className="flex items-baseline justify-between border-t border-line-strong pt-3">
                <dt className="text-micro uppercase text-ink-subtle">Total</dt>
                <dd className="font-sans text-metric tabular-nums text-ink">{formatCurrency(total)}</dd>
              </div>
            </dl>
          </section>

          <div className="sticky bottom-0 z-10 mt-10 flex justify-end gap-3 border-t border-line bg-paper/95 py-4">
            <Button variant="ghost" onClick={() => navigate('/invoices')}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-invoice-btn">
              {saving ? 'Creating…' : 'Create invoice'}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
